import styled from '@emotion/styled'
import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  type Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import { cloneElement, memo, type ReactElement, type RefAttributes, useCallback, useEffect, useState } from 'react'

import type { RowContainerProps } from '../../components/rows/index.ts'
import { borderRadius, colorTokens, shadow, spacing } from '../../foundations/index.ts'

type DropdownProps = {
  children: ReactElement<RowContainerProps>
  trigger: ReactElement<RefAttributes<unknown>>
  placement?: Placement
  className?: string | undefined
  isInDialog?: boolean
  shouldCloseOnClick?: boolean
  onOpenChange?: ((isOpen: boolean) => void) | undefined
  /** Controls whether the dropdown is open; it then opens and closes only through `onOpenChange`. */
  open?: boolean | undefined
}

export const Dropdown = memo<DropdownProps>(function Dropdown(props) {
  const {
    children,
    trigger,
    placement,
    className,
    isInDialog = false,
    shouldCloseOnClick = false,
    onOpenChange,
    open,
  } = props

  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const isOpen = open ?? uncontrolledOpen

  const handleOpenChange = useCallback(
    (newIsOpen: boolean) => {
      setUncontrolledOpen(newIsOpen)
      onOpenChange?.(newIsOpen)
    },
    [onOpenChange],
  )

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: handleOpenChange,
    placement: placement ?? 'bottom-start',
    // Make sure the tooltip stays on the screen
    whileElementsMounted: autoUpdate,
    middleware: [
      flip({
        fallbackAxisSideDirection: 'start',
        fallbackStrategy: 'initialPlacement',
      }),
      shift(),
      offset(8),
    ],
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss])
  const [portalRoot, setPortalRoot] = useState(document.body)

  useEffect(() => {
    // We need this to force the dropdown to be rendered in the same context as the dialog else it appears behind the dialog
    // The reason is both the dialog and the dropdown are using FloatingPortal to render the content
    if (!isInDialog) {
      return
    }

    // Find the closest MUI Dialog
    const dialogElement = document.querySelector('.MuiDialog-root') as HTMLElement | null

    // Set the portal root to Dialog or default to body
    if (dialogElement) {
      setPortalRoot(dialogElement)
    }
  }, [isInDialog])

  return (
    <>
      {/* eslint-disable-next-line react-hooks/refs */}
      {cloneElement(trigger, { ref: refs.setReference, ...getReferenceProps() })}
      {isOpen ? (
        <FloatingPortal root={portalRoot}>
          <DropdownContainer
            // eslint-disable-next-line react-hooks/refs
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={className}
            onBlur={() => handleOpenChange(false)}
            onClick={() => shouldCloseOnClick && handleOpenChange(false)}
          >
            <DropdownScrollViewport>
              <DropdownScrollContent>{cloneElement(children, { variant: 'dropdown' })}</DropdownScrollContent>
            </DropdownScrollViewport>
          </DropdownContainer>
        </FloatingPortal>
      ) : null}
    </>
  )
})

const DropdownContainer = styled.div`
  position: absolute;
  z-index: 1;

  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 0 0;
  padding: ${spacing['30']};
  gap: ${spacing['30']};

  border-radius: ${borderRadius['20']};
  border: 1px solid ${colorTokens['color-stroke-base-basic']};
  background: ${colorTokens['color-bg-base-normal']};
  box-shadow: ${shadow.bottom[30]};
`

const DropdownScrollViewport = styled.div`
  width: calc(100% + ${spacing['20']});
  margin-right: calc(-1 * ${spacing['20']});
  max-height: 30vh;
  overflow: hidden auto;

  scrollbar-width: thin;
  scrollbar-color: ${colorTokens['color-bg-base-active-disable']} transparent;

  &::-webkit-scrollbar {
    width: ${spacing['20']};
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 6px;
    background: ${colorTokens['color-bg-base-active-disable']};
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
`

const DropdownScrollContent = styled.div`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding-right: ${spacing['20']};
`
